import classNames from 'classnames';
import { useState } from 'react';

import {
  DIVERGING_BAR_HEADER_LABEL_HEIGHT,
  DIVERGING_BAR_LABEL_GAP,
  DivergingBarChart,
  type DivergingBarData,
  type DivergingBarLayout,
  FormatType,
  getPrimaryColor,
  getPrimaryColorDark,
  SENTIMENT_NEGATIVE_COLOR_DARK,
  SENTIMENT_NEGATIVE_COLOR_LIGHT,
  SENTIMENT_POSITIVE_COLOR,
  useChartTheme,
  withExplicitSign,
} from '@revelio/replots';
import { Text } from '@revelio/ui';

import { usePlotMetric } from '../../../hooks/usePlotMetric';
import type { BreakdownSingleDataSet } from '../../DataProvider/types';
import { isSingleDataSet } from '../../DataProvider/types';
import type { DivergingBarComboSortKey } from '../../PageConfigProvider/types/item';
import { isTransitionMetric } from '../../PageConfigProvider/types/metric';
import { ChartNotSupported } from '../ChartNotSupported';
import { useChart } from '../ChartProvider';
import { getPrettyFormatValue } from '../utils/format';
import { getFormat } from '../utils/getFormat';
import { getDivergingBarConfig } from '../utils/map-to-diverging-data';
import { transformComparisonData } from '../utils/transform-comparison-data';
import { useActiveColors } from '../utils/useActiveColors';
import styles from './DivergingBarCombo.module.css';

const FOOTER_HEIGHT = 25;

function sortByLabelOrder<T extends { label: string }>(
  data: T[],
  labelOrder: string[]
): T[] {
  const orderMap = new Map(labelOrder.map((l, i) => [l, i]));
  return [...data].sort(
    (a, b) =>
      (orderMap.get(a.label) ?? Infinity) - (orderMap.get(b.label) ?? Infinity)
  );
}

function getSortValue(
  data: DivergingBarData,
  by: DivergingBarComboSortKey
): number {
  const left = data.leftValue ?? 0;
  const right = data.rightValue ?? 0;
  switch (by) {
    case 'left':
      return left;
    case 'right':
      return right;
    case 'diff':
      return left + right;
    case 'sum':
      return Math.abs(left) + Math.abs(right);
  }
}

export const DivergingBarCombo = ({
  dataSet,
  formatOverride,
}: {
  dataSet: BreakdownSingleDataSet;
  formatOverride?: FormatType;
}) => {
  const { item } = useChart();
  const metric = usePlotMetric();
  const { colorMap } = useActiveColors();
  const { theme } = useChartTheme();
  const isDark = theme === 'dark';
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const sentimentNegativeColor =
    theme === 'dark'
      ? SENTIMENT_NEGATIVE_COLOR_DARK
      : SENTIMENT_NEGATIVE_COLOR_LIGHT;
  const format = (() => {
    if (formatOverride !== undefined) {
      return formatOverride;
    }
    const plotFormat = item?.plot?.format;
    if (plotFormat !== undefined) {
      return plotFormat;
    }
    return getFormat({ metric });
  })();

  const differenceFormat = (() => {
    if (format === FormatType.PERCENTAGE) {
      return FormatType.PERCENTAGE_POINT;
    }
    if (metric === 'transitions_inflows_and_outflows') {
      return FormatType.SI;
    }
    return FormatType.DECIMAL;
  })();

  const showBarLabels = item?.plot?.showDataLabels ?? false;
  const baseline = item?.plot?.comparisonTransformation?.baseline ?? 'second';

  const tornadoConfig = getDivergingBarConfig(
    dataSet,
    item,
    colorMap,
    getPrettyFormatValue(format),
    { baseline, dualViewColors: true }
  );

  const transformedDifference = transformComparisonData(dataSet, {
    baseline,
  });
  if (!isSingleDataSet(transformedDifference)) {
    return (
      <ChartNotSupported>Incompatible data for comparison</ChartNotSupported>
    );
  }

  const differenceDataSet = transformedDifference;
  const differenceFormatValue = (() => {
    const base = getPrettyFormatValue(differenceFormat);
    if (metric === 'transitions_inflows_and_outflows') {
      return withExplicitSign(base);
    }
    return base;
  })();
  const differenceConfig = getDivergingBarConfig(
    differenceDataSet,
    item,
    colorMap,
    differenceFormatValue
  );

  const comboSort = item?.plot?.divergingBarCombo?.sort;
  const sortedLabels = (() => {
    if (!comboSort) {
      // Sort descending by absolute baseline share so the highest-share row appears first.
      // tornadoConfig stores leftValue as the negated baseline share,
      // so Math.abs recovers the original share magnitude.
      return [...tornadoConfig.divergingBarData]
        .sort((a, b) => Math.abs(b.leftValue ?? 0) - Math.abs(a.leftValue ?? 0))
        .map((d) => d.label);
    }
    const source =
      comboSort.by === 'diff'
        ? differenceConfig.divergingBarData
        : tornadoConfig.divergingBarData;
    const directionFactor = comboSort.direction === 'asc' ? 1 : -1;
    return [...source]
      .sort(
        (a, b) =>
          directionFactor *
          (getSortValue(a, comboSort.by) - getSortValue(b, comboSort.by))
      )
      .map((d) => d.label);
  })();

  const tornadoDataSorted = sortByLabelOrder(
    tornadoConfig.divergingBarData,
    sortedLabels
  );
  const differenceDataSorted = sortByLabelOrder(
    differenceConfig.divergingBarData,
    sortedLabels
  );

  const combo = item?.plot?.divergingBarCombo;
  const comparisonLabel = combo?.comparisonLabel?.trim() ?? '';
  const differenceLabel = combo?.differenceLabel?.trim() ?? '';

  const renderCenteredFooter =
    (label: string) => (_layout: DivergingBarLayout) => (
      <div className={styles.footerRow}>
        <Text fontSize="x-sm" className={styles.footerLabel}>
          {label}
        </Text>
      </div>
    );

  const showEitherFooter = comparisonLabel !== '' || differenceLabel !== '';
  const showLeftFooter = comparisonLabel !== '';
  const showRightFooter = differenceLabel !== '';

  const isTransition = isTransitionMetric(metric);

  const tornadoByLabel = (() => {
    const map = new Map<string, DivergingBarData>();
    for (const row of tornadoDataSorted) {
      map.set(row.label, row);
    }
    return map;
  })();
  const differenceByLabel = (() => {
    const map = new Map<string, DivergingBarData>();
    for (const row of differenceDataSorted) {
      map.set(row.label, row);
    }
    return map;
  })();

  const tooltipBaseFormatType =
    format === FormatType.SI ? FormatType.INTEGER : format;
  const formatTooltipBase = getPrettyFormatValue(tooltipBaseFormatType);

  const tooltipDifferenceFormatType =
    differenceFormat === FormatType.SI ? FormatType.INTEGER : differenceFormat;
  const formatTooltipDifference = (() => {
    const base = getPrettyFormatValue(tooltipDifferenceFormatType);
    if (isTransition) {
      return withExplicitSign(base);
    }
    return base;
  })();

  const differenceLineLabel = isTransition ? 'Change' : 'Difference';

  const renderRowTooltip = (data: DivergingBarData) => {
    const tornadoRow = tornadoByLabel.get(data.label);
    const diffRow = differenceByLabel.get(data.label);

    const hasDiff = diffRow !== undefined;
    const diffValue = diffRow
      ? (diffRow.leftValue ?? 0) + (diffRow.rightValue ?? 0)
      : 0;

    const leftRaw = tornadoRow?.leftValue;
    const rightRaw = tornadoRow?.rightValue;
    const hasLeft = typeof leftRaw === 'number';
    const hasRight = typeof rightRaw === 'number';

    return (
      <div className={styles.tooltipBlock}>
        <div className={styles.tooltipTitle}>{data.label}</div>
        <dl className={styles.tooltipMetrics}>
          {hasRight && tornadoRow && (
            <>
              <dt>{tornadoRow.rightLabel}</dt>
              <dd>{formatTooltipBase(Math.abs(rightRaw))}</dd>
            </>
          )}
          {hasLeft && tornadoRow && (
            <>
              <dt>{tornadoRow.leftLabel}</dt>
              <dd>{formatTooltipBase(Math.abs(leftRaw))}</dd>
            </>
          )}
          {hasDiff && (
            <>
              <dt>{differenceLineLabel}</dt>
              <dd>{formatTooltipDifference(diffValue)}</dd>
            </>
          )}
        </dl>
      </div>
    );
  };

  const tornadoLeftHeaderLabel = (() => {
    const configured = combo?.leftHeaderLabel?.trim();
    if (configured) return configured;
    return tornadoConfig.leftFooterLabel;
  })();
  const tornadoRightHeaderLabel = (() => {
    const configured = combo?.rightHeaderLabel?.trim();
    if (configured) return configured;
    return tornadoConfig.rightFooterLabel;
  })();

  const comparisonFocusColor = isDark
    ? getPrimaryColorDark()
    : getPrimaryColor();

  const tornadoHasHeaderLabels =
    (tornadoLeftHeaderLabel !== undefined && tornadoLeftHeaderLabel !== '') ||
    (tornadoRightHeaderLabel !== undefined && tornadoRightHeaderLabel !== '');
  const differenceTopOffset = tornadoHasHeaderLabels
    ? DIVERGING_BAR_HEADER_LABEL_HEIGHT + DIVERGING_BAR_LABEL_GAP
    : 0;

  return (
    <div className={styles.comboRoot}>
      <div className={classNames(styles.comboPane, styles.tornadoPane)}>
        <div className={styles.paneBody}>
          <DivergingBarChart
            data={tornadoDataSorted}
            format={format}
            leftColor={comparisonFocusColor}
            rightColor={comparisonFocusColor}
            tooltipContent={renderRowTooltip}
            activeLabel={hoveredLabel}
            onHoveredLabelChange={setHoveredLabel}
            showBarLabels={showBarLabels}
            valueLabel={tornadoConfig.valueLabel}
            sortData={false}
            leftHeaderLabel={tornadoLeftHeaderLabel}
            rightHeaderLabel={tornadoRightHeaderLabel}
            maxLabelWidthRatio={0.25}
            minLabelWidth={120}
            verticalLayout="fill"
            barCornerRadius={0}
            centerGap={6}
            barHeightRatio={0.65}
            barLabelPosition="outside"
            barLabelColor="var(--ui-text-normal)"
            footer={
              showLeftFooter ? renderCenteredFooter(comparisonLabel) : undefined
            }
            footerHeight={showEitherFooter ? FOOTER_HEIGHT : undefined}
          />
        </div>
      </div>
      <div className={classNames(styles.comboPane, styles.differencePane)}>
        <div className={styles.paneBody}>
          <DivergingBarChart
            data={differenceDataSorted}
            format={differenceFormat}
            leftColor={sentimentNegativeColor}
            rightColor={SENTIMENT_POSITIVE_COLOR}
            tooltipContent={renderRowTooltip}
            activeLabel={hoveredLabel}
            onHoveredLabelChange={setHoveredLabel}
            showBarLabels={showBarLabels}
            valueLabel={differenceConfig.valueLabel}
            sortData={false}
            topOffset={differenceTopOffset}
            showYAxisLabels={false}
            barOpacity={0.9}
            verticalLayout="fill"
            barCornerRadius={0}
            barHeightRatio={0.65}
            barLabelPosition="outside"
            labelReserveRatio={0.6}
            footer={
              showRightFooter
                ? renderCenteredFooter(differenceLabel)
                : undefined
            }
            footerHeight={showEitherFooter ? FOOTER_HEIGHT : undefined}
          />
        </div>
      </div>
    </div>
  );
};
